import { useEffect, useState } from 'react';
import { api } from '../../../api/client';
import styles from './MealsPage.module.css';

type DayMenu = { breakfast: string; lunch: string; snack: string };
type WeeklyMenu = Record<string, DayMenu>;

const DAYS = [
  { key: 'monday', label: 'Lunes' },
  { key: 'tuesday', label: 'Martes' },
  { key: 'wednesday', label: 'Miércoles' },
  { key: 'thursday', label: 'Jueves' },
  { key: 'friday', label: 'Viernes' },
];

function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function formatDate(d: Date): string {
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
}

export default function MealsPage() {
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [menu, setMenu] = useState<WeeklyMenu>({});
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const emptyDay = (): DayMenu => ({ breakfast: '', lunch: '', snack: '' });

  useEffect(() => {
    loadWeek(weekStart);
  }, []);

  const loadWeek = async (start: Date) => {
    setLoading(true);
    setError('');
    try {
      const data = (await api.get('/api/security/meal-plans', {
        params: { weekStart: start.toISOString() },
      })) as any;
      setMenu(data.menu || {});
      setNotes(data.notes || '');
    } catch {
      setError('Error al cargar menú');
    } finally {
      setLoading(false);
    }
  };

  const navWeek = (dir: number) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + dir * 7);
    setWeekStart(d);
    loadWeek(d);
  };

  const updateDay = (dayKey: string, meal: string, value: string) => {
    setMenu((prev) => ({
      ...prev,
      [dayKey]: { ...(prev[dayKey] || emptyDay()), [meal]: value },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/api/security/meal-plans', {
        weekStart: weekStart.toISOString(),
        menu,
        notes,
      });
      setSuccess('Menú guardado correctamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Error al guardar menú');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>🍽️ Menú de Alimentos</h1>
      <p className={styles.subtitle}>Plan semanal de comidas para comunicar a los padres</p>

      <div className={styles.weekNav}>
        <button onClick={() => navWeek(-1)} className={styles.navBtn}>
          ← Semana anterior
        </button>
        <span className={styles.weekLabel}>Semana del {formatDate(weekStart)}</span>
        <button onClick={() => navWeek(1)} className={styles.navBtn}>
          Semana siguiente →
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {success && <div className={styles.success}>{success}</div>}

      {loading ? (
        <p className={styles.loading}>Cargando...</p>
      ) : (
        <div className={styles.grid}>
          {DAYS.map((day) => {
            const dayMenu = menu[day.key] || emptyDay();
            return (
              <div key={day.key} className={styles.dayCard}>
                <h3 className={styles.dayName}>{day.label}</h3>
                <div className={styles.mealField}>
                  <label>Desayuno</label>
                  <input
                    type="text"
                    value={dayMenu.breakfast}
                    onChange={(e) => updateDay(day.key, 'breakfast', e.target.value)}
                    placeholder="Ej: Cereal con leche"
                  />
                </div>
                <div className={styles.mealField}>
                  <label>Almuerzo</label>
                  <input
                    type="text"
                    value={dayMenu.lunch}
                    onChange={(e) => updateDay(day.key, 'lunch', e.target.value)}
                    placeholder="Ej: Pollo asado con ensalada"
                  />
                </div>
                <div className={styles.mealField}>
                  <label>Merienda</label>
                  <input
                    type="text"
                    value={dayMenu.snack}
                    onChange={(e) => updateDay(day.key, 'snack', e.target.value)}
                    placeholder="Ej: Fruta de temporada"
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className={styles.notesSection}>
        <label className={styles.notesLabel}>Notas generales</label>
        <textarea
          className={styles.notesInput}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Alergias, cambios, información adicional..."
          rows={3}
        />
      </div>

      <button onClick={handleSave} disabled={saving} className={styles.saveBtn}>
        {saving ? 'Guardando...' : 'Guardar Menú'}
      </button>
    </div>
  );
}
